/**
 * Frontend Debug Mode — browser-console flow tracing.
 *
 * Purely a developer aid: when enabled, it prints a structured trace of the
 * app flow (web service calls, component lifecycles, state transitions) to the
 * browser console. It does NOTHING to the UI and is a no-op when disabled.
 *
 * Enable / disable (persists in localStorage, no rebuild needed):
 *   window.tipddyDebug.enable()
 *   window.tipddyDebug.disable()
 *   window.tipddyDebug.status()
 *
 * Or force it on at build time with:  REACT_APP_DEBUG=true
 */

const STORAGE_KEY = "tipddy:debug";

const LEVELS = {
  info: { label: "•", css: "color:#2b8fbf;font-weight:700" },
  success: { label: "✓", css: "color:#3a9d5d;font-weight:700" },
  warn: { label: "▲", css: "color:#c9821f;font-weight:700" },
  error: { label: "✕", css: "color:#c0392b;font-weight:700" },
};

const SCOPE_CSS = "color:#8a6d3b;font-weight:700";
const MSG_CSS = "color:inherit;font-weight:400";

/** Read the flag fresh every time so runtime toggling takes effect at once. */
export const isDebugEnabled = () => {
  if (process.env.REACT_APP_DEBUG === "true") return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

export const enableDebug = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch (e) {
    /* ignore */
  }
  // eslint-disable-next-line no-console
  console.log("%c[tipddy] debug mode ON", "color:#3a9d5d;font-weight:700");
  return true;
};

export const disableDebug = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  // eslint-disable-next-line no-console
  console.log("%c[tipddy] debug mode OFF", "color:#7a7a7a;font-weight:700");
  return false;
};

const emit = (level, scope, message, data) => {
  if (!isDebugEnabled()) return;
  const { label, css } = LEVELS[level] || LEVELS.info;
  const prefix = `%c[tipddy]%c ${label} %c${scope}%c ${message}`;
  const styles = ["color:#b0703a;font-weight:800", "", css, SCOPE_CSS, MSG_CSS];
  const fn =
    // eslint-disable-next-line no-console
    level === "error" ? console.error : level === "warn" ? console.warn : console.log;
  if (data !== undefined) {
    fn(prefix, ...styles, data);
  } else {
    fn(prefix, ...styles);
  }
};

/**
 * Create a scoped logger, e.g. createLogger("GlobalRanking").
 */
export const createLogger = (scope) => ({
  info: (message, data) => emit("info", scope, message, data),
  success: (message, data) => emit("success", scope, message, data),
  warn: (message, data) => emit("warn", scope, message, data),
  error: (message, data) => emit("error", scope, message, data),
  group: (label) => {
    if (!isDebugEnabled()) return;
    // eslint-disable-next-line no-console
    console.groupCollapsed(`%c[tipddy]%c ${scope} — ${label}`, "color:#b0703a;font-weight:800", SCOPE_CSS);
  },
  groupEnd: () => {
    if (!isDebugEnabled()) return;
    // eslint-disable-next-line no-console
    console.groupEnd();
  },
});

/**
 * Wrap an async operation to trace request → response/error + elapsed time.
 *
 *   await traceCall("ws:get_ranking", { courseid: 0 }, () => axios.get(...));
 */
export const traceCall = async (scope, meta, fn) => {
  const log = createLogger(scope);
  log.group("call");
  log.info("request", meta);
  const started =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  try {
    const result = await fn();
    const ms = (
      (typeof performance !== "undefined" ? performance.now() : Date.now()) -
      started
    ).toFixed(1);
    log.success(`response (${ms} ms)`, result);
    return result;
  } catch (error) {
    log.error("failed", error);
    throw error;
  } finally {
    log.groupEnd();
  }
};

// Expose a runtime handle so it can be toggled straight from the console.
if (typeof window !== "undefined") {
  window.tipddyDebug = {
    enable: enableDebug,
    disable: disableDebug,
    status: () => {
      const on = isDebugEnabled();
      // eslint-disable-next-line no-console
      console.log(`%c[tipddy] debug is ${on ? "ON" : "OFF"}`, "color:#b0703a;font-weight:700");
      return on;
    },
  };
  if (!isDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log(
      "%c[tipddy]%c debug mode available — run %cwindow.tipddyDebug.enable()%c to trace the flow",
      "color:#b0703a;font-weight:800",
      "color:#7a7a7a",
      "color:#2b8fbf;font-weight:700",
      "color:#7a7a7a"
    );
  }
}
